import { useState } from 'react';
import { useCurrentUser } from '../account/useAuth';
import { useClassroomControl } from '../teacher/useTeacher';
import { useFeedback } from '../teacher/useTeacher';
import { submissionBlobUrl } from '../teacher/teacherApi';
import './ClassroomBroadcastBanner.css';

/**
 * Soft spotlight banner shown to a classroom kid when the teacher
 * broadcasts one submission to the whole class.
 *
 * PLAN.md framing: "real-time push of one kid's project to the
 * whole class." The teacher clicks "📡 Broadcast to class" on a
 * grading page; every kid in the room sees this banner appear at
 * the top of their studio with the spotlighted kid's name + project.
 *
 * Different from the pause overlay:
 *   * Pause is a hard freeze — the kid CAN'T capture or train.
 *   * Broadcast is a soft pointer — the kid keeps working but is
 *     drawn to "look at Avery's birds." Click expands a panel
 *     showing the same per-class samples the teacher saw at
 *     grading time, so the broadcast is a real artefact (not just
 *     an attention call).
 *
 * Mounted globally from `App.tsx` (alongside ClassroomPauseOverlay)
 * so it follows the kid wherever they navigate inside the studio.
 * Hidden for accounts without a `classroom_id`.
 */
export function ClassroomBroadcastBanner(): JSX.Element | null {
  const { data: user } = useCurrentUser();
  const control = useClassroomControl(user?.classroom_id ?? null);
  const [expanded, setExpanded] = useState(false);

  if (!user?.classroom_id) return null;
  const broadcast = control.data?.broadcast;
  if (!broadcast) return null;

  const teacherName = broadcast.submitter_display_name ?? 'a classmate';

  return (
    <div
      className="bl-bcast"
      role="region"
      aria-label="Class spotlight"
      data-testid="classroom-broadcast-banner"
    >
      <div className="bl-bcast__head">
        <span className="bl-bcast__icon" aria-hidden="true">
          📡
        </span>
        <div className="bl-bcast__copy">
          <strong className="bl-bcast__title">
            Look at {teacherName}&apos;s {broadcast.project_kind} project
          </strong>
          <span className="bl-bcast__subtitle">
            {broadcast.project_name} — your teacher put it on the board.
          </span>
        </div>
        <button
          type="button"
          className="bl-bcast__toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls="bl-bcast-panel"
        >
          {expanded ? 'Hide' : 'Take a look →'}
        </button>
      </div>

      {expanded ? (
        <BroadcastPanel classroomId={user.classroom_id} submissionId={broadcast.submission_id} />
      ) : null}
    </div>
  );
}

function BroadcastPanel({
  classroomId,
  submissionId,
}: {
  classroomId: string;
  submissionId: string;
}): JSX.Element {
  // Pulls the same FeedbackRead the teacher's grading page reads —
  // the kid is class member, so the endpoint authorises. We're
  // intentionally surfacing the kid's classes + samples (not the
  // rubric marks) so this panel stays a "look at the work" view,
  // not a "watch your classmate get graded" voyeur.
  const fb = useFeedback(classroomId, submissionId);
  if (fb.isPending) {
    return (
      <div id="bl-bcast-panel" className="bl-bcast__panel">
        <p className="bl-bcast__quiet">Loading…</p>
      </div>
    );
  }
  if (fb.isError || !fb.data) {
    return (
      <div id="bl-bcast-panel" className="bl-bcast__panel">
        <p className="bl-bcast__quiet">Couldn&apos;t load the spotlight project right now.</p>
      </div>
    );
  }
  const data = fb.data;
  return (
    <div id="bl-bcast-panel" className="bl-bcast__panel">
      {data.project_classes.length === 0 ? (
        <p className="bl-bcast__quiet">
          No samples to show yet — your classmate is still building their project.
        </p>
      ) : (
        <ul className="bl-bcast__class-list">
          {data.project_classes.map((c) => (
            <li key={c.class_label} className="bl-bcast__class-row">
              {c.sample_blob_id && (c.sample_kind === 'image' || c.sample_kind === 'pose') ? (
                <img
                  className="bl-bcast__class-thumb"
                  src={submissionBlobUrl(classroomId, submissionId, c.sample_blob_id)}
                  alt={`${c.class_label} sample`}
                  loading="lazy"
                />
              ) : c.sample_blob_id && c.sample_kind === 'audio' ? (
                // eslint-disable-next-line jsx-a11y/media-has-caption -- raw kid sample, no captions to attach
                <audio
                  className="bl-bcast__class-audio"
                  src={submissionBlobUrl(classroomId, submissionId, c.sample_blob_id)}
                  controls
                  preload="metadata"
                  aria-label={`Play ${c.class_label} sample`}
                />
              ) : (
                <div
                  className="bl-bcast__class-thumb bl-bcast__class-thumb--missing"
                  aria-hidden="true"
                />
              )}
              <div className="bl-bcast__class-meta">
                <span className="bl-bcast__class-name">{c.class_label}</span>
                <span className="bl-bcast__class-count">
                  {c.sample_count} {c.sample_count === 1 ? 'sample' : 'samples'}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
